import React, { useState } from 'react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = '';

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    contactMethod: 'Phone',
    services: [],
    message: ''
  });

  const [newsletterEmail, setNewsletterEmail] = useState('');

  const serviceOptions = [
    'On-Site Eye Exams',
    'Eyeglasses Fitting',
    'Sunglasses Fitting',
    'Glasses Repairs',
    'Contact Lenses',
    'Special Needs Optician'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        services: checked 
          ? [...prev.services, value]
          : prev.services.filter(s => s !== value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Send to backend API
    axios.post(`${BACKEND_URL}/api/contact`, formData)
      .then(response => {
        toast({
          title: "Message Sent!",
          description: "Thank you for contacting us. We will get back to you as soon as possible.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          contactMethod: 'Phone',
          services: [],
          message: ''
        });
      })
      .catch(error => {
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive"
        });
      });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    
    // Send to backend API
    axios.post(`${BACKEND_URL}/api/newsletter`, { email: newsletterEmail })
      .then(response => {
        toast({
          title: "Subscribed!",
          description: "Welcome to our newsletter! You'll receive 10% off on your next purchase.",
        });
        
        setNewsletterEmail('');
      })
      .catch(error => {
        toast({
          title: "Error",
          description: "Failed to subscribe. Please try again.",
          variant: "destructive"
        });
      });
  };

  return (
    <section id="contact-form" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get In Touch After Hours
            </h2>
            <p className="text-gray-600">
              Need to contact us but it's after hours? Fill out the form below, and we'll contact you during our next business day!
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Us</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                  <input 
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                  <input 
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone*</label>
                  <input 
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">How Do You Prefer To Be Contacted?*</label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        name="contactMethod"
                        value="Phone"
                        checked={formData.contactMethod === 'Phone'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      Phone
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        name="contactMethod"
                        value="Email"
                        checked={formData.contactMethod === 'Email'}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      Email
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service(s) You're Most Interested In*</label>
                  <div className="space-y-2">
                    {serviceOptions.map((service) => (
                      <label key={service} className="flex items-center">
                        <input 
                          type="checkbox"
                          name="services"
                          value={service}
                          checked={formData.services.includes(service)}
                          onChange={handleChange}
                          className="mr-2"
                        />
                        {service}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message (Optional)</label>
                  <textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  />
                </div>

                <button 
                  type="submit"
                  className="w-full bg-red-600 text-white py-3 rounded-md hover:bg-red-700 transition-colors font-medium"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Newsletter */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Join the club! Subscribe to our newsletter and get 10% Off!</h3>
              <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enter your email address here</label>
                  <input 
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
                <button 
                  type="submit"
                  className="w-full bg-red-600 text-white py-3 rounded-md hover:bg-red-700 transition-colors font-medium"
                >
                  Subscribe
                </button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="font-bold text-gray-900 mb-4">Visit Our Store</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We are conveniently located in Centre Plaza at 128 Queen Street South unit #21 in Streetsville, Ontario.
                  <br/><br/>
                  Main intersection is Queen St. South & Tannery St.
                  <br/><br/>
                  The same plaza as the LCBO & Shoppers Drug Mart.
                  <br/><br/>
                  Free parking is available throughout the plaza.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
